import { ArrowRight, ScanSearch, ShieldCheck, Sparkles, UsersRound, Waypoints } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { routeToIntake } from '../lib/intakeRouting';
import type { IntakeIntent } from '../lib/intakeRouting';

// Cyan-dominant transition / triage band (Visual Contract §6). Clearly bifurcates the
// two public offers and supports source-routed users (Findable, viabilAIty, direct,
// agency). Peer sources are routes, never fatal runtime dependencies (MVP Contract §8).

type Offer = {
  eyebrow: string;
  name: string;
  promise: string;
  cta: string;
  intent: IntakeIntent;
  productHint: string;
  accent: string;
  primary: boolean;
};

const offers: Offer[] = [
  {
    eyebrow: 'Primary',
    name: 'Message-to-Market by Macellum',
    promise:
      'Send proof once. Macellum turns it into website evidence, social and local updates, proof packs, and AI-readable context — visible, citable, and sales-ready.',
    cta: 'Turn proof into presence',
    intent: 'm2m',
    productHint: 'm2m_t1',
    accent: 'cyan',
    primary: true,
  },
  {
    eyebrow: 'Secondary',
    name: 'AEO Visibility Repair',
    promise:
      'Repair the machine-readable evidence layer so AI-mediated discovery systems can read, understand, and cite your business. Observable remediation — no ranking or inclusion promises.',
    cta: 'Repair my AI visibility',
    intent: 'aeo',
    productHint: 'aeo_t1',
    accent: 'violet',
    primary: false,
  },
];

const sources: Array<{ source: string; detail: string; sourceId: string; intent: IntakeIntent; icon: LucideIcon }> = [
  {
    source: 'Came from Findable',
    detail: 'Bring your scan. Findable diagnoses; Macellum repairs the visibility gap.',
    sourceId: 'findable',
    intent: 'aeo',
    icon: ScanSearch,
  },
  {
    source: 'Came from viabilAIty',
    detail: 'High-trust venture context routes to a tailored proof-to-presence build.',
    sourceId: 'viability',
    intent: 'm2m',
    icon: ShieldCheck,
  },
  {
    source: 'Direct',
    detail: 'Trades, local services, SMEs, and founders can ask for help directly.',
    sourceId: 'macellum_direct',
    intent: 'unsure',
    icon: Waypoints,
  },
  {
    source: 'Agency partner',
    detail: 'Use Macellum as a proof production and fulfilment layer for your clients.',
    sourceId: 'agency',
    intent: 'agency',
    icon: UsersRound,
  },
];

export function OfferTriage() {
  return (
    <section
      id="offer-triage"
      className="border-y border-cyan/20 bg-obsidian py-24 text-chalk sm:py-28"
      data-aeo-section="offer-triage"
    >
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="inline-flex items-center gap-2 text-sm font-black uppercase tracking-wide text-cyan">
            <Sparkles className="size-4" aria-hidden="true" />
            Choose the right first move
          </p>
          <h2 className="mt-4 text-4xl font-black leading-tight tracking-tight sm:text-5xl">
            Two ways Macellum turns your proof into presence.
          </h2>
          <p className="mt-6 text-lg leading-8 text-ash">
            One visitor needs a proof-to-market system. Another needs the AI-visibility evidence
            layer repaired. The intake keeps your source context without forcing everyone down the
            same path.
          </p>
        </div>

        {/* Two-offer bifurcation */}
        <div className="mt-12 grid gap-5 lg:grid-cols-2">
          {offers.map((offer) => (
            <article
              key={offer.name}
              className={`flex flex-col border bg-white/[0.03] p-7 ${
                offer.primary ? 'border-cyan/50' : 'border-violet/50'
              }`}
            >
              <p
                className={`text-xs font-black uppercase tracking-wide ${
                  offer.primary ? 'text-cyan' : 'text-violet'
                }`}
              >
                {offer.eyebrow} route
              </p>
              <h3 className="mt-3 text-2xl font-black">{offer.name}</h3>
              <p className="mt-4 flex-1 text-base leading-7 text-ash">{offer.promise}</p>
              <button
                type="button"
                onClick={() => routeToIntake({ intent: offer.intent, productHint: offer.productHint })}
                className={`mt-7 inline-flex h-12 items-center justify-center gap-2 px-6 text-sm font-black uppercase tracking-wide transition ${
                  offer.primary
                    ? 'bg-cyan text-obsidian hover:bg-violet hover:text-chalk'
                    : 'border border-violet text-chalk hover:bg-violet/15'
                }`}
              >
                {offer.cta}
                <ArrowRight className="size-5" aria-hidden="true" />
              </button>
            </article>
          ))}
        </div>

        {/* Source-aware routes */}
        <div className="mt-12">
          <p className="text-sm font-black uppercase tracking-wide text-ash">Where did you come from?</p>
          <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {sources.map((route) => {
              const Icon = route.icon;
              return (
                <button
                  key={route.sourceId}
                  type="button"
                  onClick={() => routeToIntake({ intent: route.intent, sourceId: route.sourceId })}
                  className="group flex flex-col border border-white/12 bg-white/[0.03] p-5 text-left transition hover:border-cyan/60 hover:bg-white/[0.06]"
                >
                  <Icon className="size-6 text-cyan" aria-hidden="true" />
                  <h4 className="mt-4 text-base font-black text-chalk">{route.source}</h4>
                  <p className="mt-2 text-sm leading-6 text-ash">{route.detail}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-xs font-black uppercase tracking-wide text-cyan opacity-0 transition group-hover:opacity-100">
                    Route my intake
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </span>
                </button>
              );
            })}
          </div>
          <p className="mt-6 max-w-3xl text-sm leading-6 text-ash/80">
            Findable and viabilAIty enrich your intake but are never required. If their context is
            missing, Macellum still routes you through a manual review.
          </p>
        </div>
      </div>
    </section>
  );
}
